/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    bool,
    object,
} from 'prop-types';
import Slider from 'react-slick';
import useStyles from 'isomorphic-style-loader/useStyles';
import ReactHtmlParser from 'react-html-parser';
import { useDispatch, useSelector } from 'react-redux';
import Media from 'react-media';
import style from './BYODecorativeSlider.module.css';
import Dialog from '../../../../../UniversalTemplate/Dialog/Dialog';
import BYODetailsDialog from './BYODetailsDialog';
import byoTheme from './BYOTheme';
import { getBrand } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import ResponsiveImage from '../../../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import { setSelectedBYOItems } from '../../../../../../../../state/ducks/App/App-Actions';
import { trackEvent } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import useSSRMediaQuery from '../../../../../../../helpers/hooks/useSSRMediaQuery';

const BYODecorativeSlider = ({ groupData, selectedBand, decorationBandMandatory }) => {
    const [open, setModelOpen] = useState(false);
    const sliderRef = useRef(null);
    const [selectedOption, setSelectedOption] = useState({});
    useStyles(style);
    const brandData = useSelector(getBrand);
    const deviceType = useSSRMediaQuery();

    const brandCode = brandData?.code;
    const {
        buttonColorSelect = '#512C1C',
    } = byoTheme[brandCode] || {};

    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setStartX(e.clientX);
    };

    const handleMouseUp = (e) => {
        const distance = Math.abs(e.clientX - startX);
        if (distance > 5) {  // 5 is a threshold, adjust as needed
            setIsDragging(true);
        } else {
            setIsDragging(false);
        }
    };

    const setOpen = (e, toggle) => {
        if (isDragging) {
            e.stopPropagation();
            e.preventDefault();
        } else {
            setModelOpen(toggle);
        }
        setIsDragging(false);
    };
    const selectedBYODispatch = useDispatch();
    const handleSelectBand = (groupName, bandID, productCode, bandName) => {
        if (groupName && bandID && bandName) {
            selectedBYODispatch(setSelectedBYOItems({
                ...selectedBand,
                band: {
                    groupName, itemID: bandID, productCode, bandName,
                },
            }));
        }
    };
    useEffect(() => {
        if (groupData?.components?.length === 1 && decorationBandMandatory) {
            handleSelectBand(groupData?.name, groupData?.components?.[0]?.legacyId, groupData?.components?.[0]?.id, groupData?.components?.[0]?.name);
        }
    }, [groupData]);

    const addRemoveItem = (data) => {
        if (selectedBand?.band?.itemID === data?.legacyId) {
            selectedBYODispatch(setSelectedBYOItems({
                ...selectedBand,
                band: {
                    groupName: '', itemID: '', productCode: '', bandName: '',
                },
            }));
            selectedBYODispatch(trackEvent({
                ga_event_name: 'byo_interaction',
                eventCategory: 'BYO',
                eventAction: 'Decoration item clicked - Removed',
                eventLabel: `${data?.name} - ${data?.partNumber}` || '',

                interaction_type: 'remove_item',
                byo_step: '',
                byo_item_name: data?.name,
                byo_item_variant: data?.partNumber,
                byo_item_quantity: '',
            }));
        } else {
            handleSelectBand(groupData.name, data?.legacyId, data?.id, data?.name);
            selectedBYODispatch(trackEvent({
                ga_event_name: 'byo_interaction',
                eventCategory: 'BYO',
                eventAction: 'Decoration item clicked - Added',
                eventLabel: `${data?.name} - ${data?.partNumber}` || '',

                interaction_type: 'add_item',
                byo_step: '',
                byo_item_name: data?.name,
                byo_item_variant: data?.partNumber,
                byo_item_quantity: '',
            }));

            selectedBYODispatch(trackEvent({
                eventCategory: 'BYO – Select',
                eventAction: `Decoration - ${data?.name}`,
                eventLabel: data?.partNumber || '',
                eventName: 'BYO_select_decoration',
                byo_decoration_name: data?.name,
                byo_variant: data?.partNumber,
            }));
        }
    };
    const handlePopup = (e, item) => {
        setSelectedOption(item);
        setOpen(e, true);
        selectedBYODispatch(trackEvent({
            ga_event_name: 'byo_interaction',
            eventCategory: 'BYO',
            eventAction: 'Decoration popup click - open',
            eventLabel: `${item?.name} - ${item?.partNumber}` || '',
        }));

        selectedBYODispatch(trackEvent({
            eventCategory: 'BYO – View',
            eventAction: `Decoration  - ${item?.name}`,
            eventLabel: item?.partNumber || '',
            eventName: 'BYO_view_decoration',
            byo_variant: item?.partNumber,
            byo_decoration_name: item?.name,

            interaction_type: 'byo_view_item',
            byo_step: '',
            byo_item_name: item?.name,
            byo_item_variant: item?.partNumber,
            byo_item_quantity: '',
        }));
    };

    const onClose = () => setModelOpen(false);

    const renderDialog = () => (
        <BYODetailsDialog
            selectedOption={selectedOption}
            added={selectedBand?.band?.itemID === selectedOption?.legacyId}
            addRemoveItem={addRemoveItem}
        />
    );
    const renderLayout = () => (
        groupData.components.map((item) => {
            const itemImagePath = item.image?.path?.replace(/(^\w+:|^)/, '');
            const itemImageName = item.image?.name;
            const imagePath = `${itemImagePath}${itemImageName}x.jpg`;
            return (
                <div className={style.scrollableSection} role="presentation" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
                    <div
                        role="presentation"
                        className={style.imageContainer}
                        onClick={(e) => {
                            handlePopup(e, item);
                        }}
                    >
                        <ResponsiveImage className={style.itemImage} path={imagePath} alt={item.name} params={{ desktop: 'quality=100', tablet: 'quality=100', mobile: 'quality=100' }} />
                    </div>
                    <div style={{
                        '--btn-selected-color': buttonColorSelect,
                    }}
                    >
                        <button
                            type="button"
                            data-testid="cyoBandButton"
                            variant="outlined"
                            className={style.buttonContainer}
                            onClick={() => addRemoveItem(item)}
                        >
                            {selectedBand?.band?.itemID === item.legacyId ? (
                                <div className={style.selectedButton}>
                                    Selected
                                    <svg focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                                </div>
                            ) : (
                                'Select'
                            )}
                        </button>

                        <span
                            className={style.itemName}
                            onKeyDown={() => { }}
                            tabIndex={-1}
                            role="button"
                            onClick={(e) => {
                                handlePopup(e, item);
                            }}
                        >
                            {ReactHtmlParser(item?.name)}
                        </span>
                    </div>
                </div>
            );
        })
    );
    return (
        <>
            <Media query={{ maxWidth: 600 }} defaultMatches={deviceType === 'mobile'}>
                {(matches) => (matches ? <div className={style.itemsContainer}>{renderLayout()}</div>
                    : (
                        <Slider
                            ref={sliderRef}
                            className={style.sliderContainer}
                            draggable
                            dots={false}
                            infinite={false}
                            speed={500}
                            slidesToShow={3.5}
                            slidesToScroll={3}
                            arrows
                            style={{
                                '--btn-selected-color': buttonColorSelect,
                            }}
                            nextArrow={<div><svg focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M6.49 20.13l1.77 1.77 9.9-9.9-9.9-9.9-1.77 1.77L14.62 12l-8.13 8.13z" /></svg></div>}
                            prevArrow={<div><svg focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M17.51 3.87L15.73 2.1 5.84 12l9.9 9.9 1.77-1.77L9.38 12l8.13-8.13z" /></svg></div>}
                            responsive={[
                                {
                                    breakpoint: 600,
                                    settings: {
                                        slidesToShow: 2.75,
                                        slidesToScroll: 2,
                                    },
                                },
                            ]}
                        >
                            {renderLayout()}
                        </Slider>
                    ))}
            </Media>
            {open && <Dialog isOpen={open} title={(<span className={style.dialogTitle}>Item Details</span>)} onClose={onClose}>{renderDialog()}</Dialog>}
        </>
    );
};

BYODecorativeSlider.propTypes = {
    groupData: object.isRequired,
    selectedBand: object.isRequired,
    decorationBandMandatory: bool.isRequired,
};

export default BYODecorativeSlider;
